import { api, flushPastes, queuePaste } from './api';
import { readCopyReceipt, writeCopyReceipt } from './clipboardReceipts';

type Observation = {
  requestId: string;
  observedAt: string;
  field: string;
  text: string;
  surface: 'account';
  parentPasteIds?: string[];
  parentInputPasteIds?: string[];
};

type Field = HTMLInputElement | HTMLTextAreaElement;
type Mark = { id: string; start: number; end: number; account: boolean };
export type CorrectionRange = {startOffset: number; endOffset: number; parentPasteIds: string[]; parentInputPasteIds: string[]};
const tracking = new WeakMap<Field, { previous: string; ranges: Mark[] }>();
const awaitingInput = new WeakMap<Field, { expected: string; start: number; length: number; id: string; account: boolean; unknown: boolean; ranges?:CorrectionRange[] }>();
const pendingEdit = new WeakMap<Field, {before:string; start:number; end:number}>();
const fieldHistory = new WeakMap<Field, {entries:{text:string;ranges:Mark[]}[];index:number}>();
const historyAction = new WeakMap<Field, 'historyUndo' | 'historyRedo'>();
function rememberRanges(field: Field, state: {previous:string;ranges:Mark[]}) {
  const history=fieldHistory.get(field) || {entries:[],index:-1};
  const snapshot={text:state.previous,ranges:state.ranges.map(r=>({...r}))};
  if (history.entries[history.index]?.text===snapshot.text) history.entries[history.index]=snapshot;
  else {
    history.entries=history.entries.slice(0,history.index+1);history.entries.push(snapshot);
    if (history.entries.length>200) history.entries.shift();
    history.index=history.entries.length-1;
  }
  fieldHistory.set(field,history);
}
export function captureFieldEdit(field: Field, start = field.selectionStart ?? 0, end = field.selectionEnd ?? start) {
  tracked(field);
  pendingEdit.set(field, {before:field.value,start,end});
}

function tracked(field: Field) {
  let state = tracking.get(field);
  if (!state) {
    state = { previous: field.value, ranges: [] };
    tracking.set(field, state);
    rememberRanges(field,state);
  }
  return state;
}

function updateRanges(field: Field) {
  const state = tracking.get(field);
  if (!state || state.previous === field.value) return;
  const action=historyAction.get(field), history=fieldHistory.get(field);
  historyAction.delete(field);
  if (action && history) {
    const step=action==='historyUndo'?-1:1;
    for (let i=history.index+step;i>=0 && i<history.entries.length;i+=step) {
      if (history.entries[i].text===field.value) {
        history.index=i;state.previous=field.value;
        state.ranges=history.entries[i].ranges.map(r=>({...r}));pendingEdit.delete(field);return;
      }
    }
  }
  const before = state.previous, after = field.value;
  let prefix = 0, suffix = 0;
  while (prefix < before.length && prefix < after.length && before[prefix] === after[prefix]) prefix++;
  while (suffix < before.length - prefix && suffix < after.length - prefix &&
    before[before.length - suffix - 1] === after[after.length - suffix - 1]) suffix++;
  const edit = pendingEdit.get(field);
  pendingEdit.delete(field);
  if (edit?.before === before) {
    const inserted = after.length - (before.length - (edit.end - edit.start));
    if (inserted >= 0 && after.slice(0, edit.start) === before.slice(0, edit.start) &&
        after.slice(edit.start + inserted) === before.slice(edit.end)) {
      prefix = edit.start; suffix = before.length - edit.end;
    }
  }
  const oldEnd = before.length - suffix, delta = after.length - before.length;
  state.ranges = state.ranges.flatMap(mark => {
    if (mark.end <= prefix) return [mark];
    if (mark.start >= oldEnd) return [{ ...mark, start: mark.start + delta, end: mark.end + delta }];
    return [
      ...(mark.start < prefix ? [{ ...mark, end: prefix }] : []),
      ...(mark.end > oldEnd ? [{ ...mark, start: oldEnd + delta, end: mark.end + delta }] : []),
    ];
  });
  state.previous = after;
  rememberRanges(field,state);
}

export function fieldPasteParents(field: Field, start: number, end: number) {
  updateRanges(field);
  const marks = tracked(field).ranges.filter(mark => mark.start < end && mark.end > start);
  return {
    parentPasteIds: [...new Set(marks.filter(mark => !mark.account).map(mark => mark.id))],
    parentInputPasteIds: [...new Set(marks.filter(mark => mark.account).map(mark => mark.id))],
  };
}
export function fieldPasteRanges(field: Field, start = 0, end = field.value.length): CorrectionRange[] {
  updateRanges(field);
  const marks=tracked(field).ranges.filter(m=>m.start<end && m.end>start);
  const points=[...new Set(marks.flatMap(m=>[Math.max(start,m.start),Math.min(end,m.end)]))].sort((a,b)=>a-b);
  const ranges:CorrectionRange[]=[];
  for (let i=1;i<points.length;i++) {
    const active=marks.filter(m=>m.start<points[i] && m.end>points[i-1]);
    if (!active.length) continue;
    ranges.push({startOffset:points[i-1]-start,endOffset:points[i]-start,
      parentPasteIds:[...new Set(active.filter(m=>!m.account).map(m=>m.id))].sort(),
      parentInputPasteIds:[...new Set(active.filter(m=>m.account).map(m=>m.id))].sort()});
  }
  return ranges;
}
export function restoreFieldPasteRanges(field: Field, text: string, ranges: CorrectionRange[]) {
  field.value = text;
  pendingEdit.delete(field);
  tracking.set(field, {previous:text,ranges:ranges.flatMap(r => [
    ...r.parentPasteIds.map(id=>({id,start:r.startOffset,end:r.endOffset,account:false})),
    ...r.parentInputPasteIds.map(id=>({id,start:r.startOffset,end:r.endOffset,account:true})),
  ])});
  rememberRanges(field,tracking.get(field)!);
}

export function markFieldPaste(field: Field, id: string, start: number, length: number, account: boolean) {
  updateRanges(field);
  tracked(field).ranges.push({ id, start, end: start + length, account });
  rememberRanges(field,tracked(field));
}

type QueueStorage={load:(id:string)=>Promise<Observation[]>;save:(id:string,items:Observation[])=>Promise<void>};
let queueStorage:QueueStorage|undefined;
let loadingProfile=false;
let storageReady:Promise<void>=Promise.resolve();
let storageWrites:Promise<void>=Promise.resolve();
export function setAccountPasteStorage(storage:QueueStorage){queueStorage=storage;}
let profile: string | null = null;
let queued: Observation[] = [];
let pendingFlush: Promise<void> | undefined;
const pendingSignIn: Observation[] = [];
const storageKey = (id: string) => `ig-input-pastes:${id}`;

function save() {
  if (!profile) return;
  if(queueStorage&&loadingProfile)return;
  if(queueStorage){const id=profile,items=structuredClone(queued);storageWrites=storageWrites.catch(()=>{}).then(()=>queueStorage!.save(id,items));void storageWrites.catch(()=>{});return;}
  try {
    if (queued.length) localStorage.setItem(storageKey(profile), JSON.stringify(queued));
    else localStorage.removeItem(storageKey(profile));
  } catch {
    // The in-memory queue remains available while the page stays open.
  }
}

function flush(): Promise<void> {
  // An explicit save/restart must join a background send before checking the
  // remaining queue. A boolean guard could report pending work while that same
  // request was still being acknowledged.
  if (pendingFlush) return pendingFlush;
  if (!profile) return Promise.resolve();
  pendingFlush = (async () => {
    try {
      await storageReady;
      await storageWrites;
      while (profile && queued.length) {
        await api('/input-pastes', 'POST', queued[0]);
        queued.shift();
        save();
      }
    } catch {
      // The exact request IDs remain for the next online action.
    }
  })().finally(() => { pendingFlush = undefined; });
  return pendingFlush;
}

export async function flushAccountInputPastes(localOnly=false) {
  await storageReady;
  if(queueStorage)save();
  await storageWrites;
  if(localOnly)return;
  await flush();
  await storageWrites;
  if (queued.length) throw new Error('Your transcript corrections are still waiting to sync. Retry before using the transcript.');
}

function setProfile(id: string, email?: string) {
  if (profile !== id) {
    profile = id;
    if(queueStorage){
      queued=[];loadingProfile=true;
      storageReady=queueStorage.load(id).then(items=>{if(profile===id){loadingProfile=false;const ids=new Set(items.map(v=>v.requestId));queued=[...items,...queued.filter(v=>!ids.has(v.requestId))];save();}});
      void storageReady.catch(()=>{});
    } else try {
      const stored: unknown = JSON.parse(localStorage.getItem(storageKey(id)) || '[]');
      queued = Array.isArray(stored) ? stored.filter((item): item is Observation =>
        !!item && typeof item === 'object' && typeof item.requestId === 'string') : [];
    } catch { queued = []; }
  }
  for (const entry of pendingSignIn)
    if (entry.field === 'Email address' && entry.text.trim().toLowerCase() === email?.trim().toLowerCase())
      queued.push(entry);
  pendingSignIn.length = 0;
  if(!queueStorage)save();
  void flush();
}

function labelFor(field: HTMLInputElement | HTMLTextAreaElement) {
  return (field.labels?.[0]?.textContent || field.getAttribute('aria-label') ||
    field.getAttribute('placeholder') || field.name || 'Text input').trim().slice(0, 160);
}

function webUrl(text: string) {
  const candidate = text.trim();
  if (/\s/.test(candidate)) return false;
  try {
    const url = new URL(candidate);
    return (url.protocol === 'https:' || url.protocol === 'http:') && !!url.hostname;
  } catch { return false; }
}

export function installAccountPasteAudit(acceptField: (field: Element) => boolean = () => true) {
  window.addEventListener('ig-profile-selected', event => {
    const value = (event as CustomEvent<{ id: string; email?: string }>).detail;
    setProfile(value.id, value.email);
  });
  window.addEventListener('ig-account-ended', () => { profile = null; queued = []; });
  window.addEventListener('online', () => void flush());
  document.addEventListener('input', event => {
    const field = event.target;
    if (!(field instanceof Element) || !acceptField(field)) return;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
    updateRanges(field);
    const pending = awaitingInput.get(field);
    if (!pending) return;
    awaitingInput.delete(field);
    if (pending.unknown && field.value === pending.expected) {
      for (const range of pending.ranges ?? [{startOffset:0,endOffset:pending.length}])
        tracked(field).ranges.push({id:pending.id,start:pending.start+range.startOffset,
          end:pending.start+range.endOffset,account:pending.account});
    }
    rememberRanges(field,tracked(field));
  }, true);
  document.addEventListener('beforeinput', event => {
    const field = event.target;
    if (!(field instanceof Element) || !acceptField(field)) return;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
    if (!tracking.has(field)) return;
    const inputType=(event as InputEvent).inputType;
    if (inputType==='historyUndo' || inputType==='historyRedo') historyAction.set(field,inputType);
    let start=field.selectionStart ?? 0, end=field.selectionEnd ?? start;
    if (start===end && (event as InputEvent).inputType==='deleteContentBackward') {
      start=Math.max(0,start-1);
      if (start>0 && /[\uDC00-\uDFFF]/.test(field.value[start])) start--;
    } else if (start===end && (event as InputEvent).inputType==='deleteContentForward') {
      end=Math.min(field.value.length,end+1);
      if (/[\uD800-\uDBFF]/.test(field.value[start])) end=Math.min(field.value.length,end+1);
    }
    captureFieldEdit(field,start,end);
  }, true);
  document.addEventListener('copy', event => {
    const field = event.target;
    if (!(field instanceof Element) || !acceptField(field)) return;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) ||
        field.closest('[data-audited-paste]') || !event.clipboardData) return;
    updateRanges(field);
    const start = field.selectionStart, end = field.selectionEnd;
    if (start == null || end == null || start === end) return;
    const text = field.value.slice(start, end);
    const parents = fieldPasteParents(field, start, end);
    writeCopyReceipt(event.clipboardData, {
      text,
      sessionId: field.closest<HTMLElement>('[data-session-id]')?.dataset.sessionId,
      ...parents,
      ranges:fieldPasteRanges(field,start,end),
    });
    event.preventDefault();
  }, true);
  document.addEventListener('paste', event => {
    const field = event.target;
    if (!(field instanceof Element) || !acceptField(field)) return;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) ||
        field.disabled || field.readOnly || field.closest('[data-audited-paste]') ||
        event.defaultPrevented) return;
    const label = labelFor(field);
    if (/password|passphrase|secret|api key|access token|authenticator|two.factor|2fa|backup code|recovery code|verification code|security code|one.time code|web address|website address|\burl\b|portal address|portal link/i.test(label) ||
        /^(password|hidden|file|url)$/i.test(field.type) ||
        /one-time-code|password/i.test(field.autocomplete)) return;
    const text = event.clipboardData?.getData('text/plain') ?? '';
    if (!text || webUrl(text)) return;
    const receipt = event.clipboardData && readCopyReceipt(event.clipboardData);
    const sessionId = field.closest<HTMLElement>('[data-session-id]')?.dataset.sessionId;
    const known = !!receipt && (!receipt.sessionId || receipt.sessionId === sessionId);
    const parentPasteIds = known ? receipt.parentPasteIds ?? [] : [];
    const parentInputPasteIds = known ? receipt.parentInputPasteIds ?? [] : [];
    const unknown = !known || parentPasteIds.length > 0 || parentInputPasteIds.length > 0;
    const start = field.selectionStart ?? field.value.length, end = field.selectionEnd ?? start;
    captureFieldEdit(field,start,end);
    const id = crypto.randomUUID();
    awaitingInput.set(field, { expected: field.value.slice(0, start) + text + field.value.slice(end),
      start, length: text.length, id, account: !sessionId, unknown,
      ...(known && receipt?.ranges ? {ranges:receipt.ranges} : {}) });
    if (sessionId) {
      try {
        queuePaste(sessionId, { requestId: id, text, destination: 'other-field',
          fieldLabel: label, origin: known ? 'document' : 'external', method: 'clipboard',
          startOffset: start,
          ...(parentPasteIds.length ? { parentPasteIds } : {}),
          ...(parentInputPasteIds.length ? { parentInputPasteIds } : {}),
        });
      } catch { /* The in-memory queue still retains the event. */ }
      void flushPastes(sessionId).catch(() => {});
      return;
    }
    if (!unknown) return;
    const observation: Observation = {
      requestId: id, observedAt: new Date().toISOString(),
      field: label, text, surface: 'account',
      ...(parentPasteIds.length ? { parentPasteIds } : {}),
      ...(parentInputPasteIds.length ? { parentInputPasteIds } : {}),
    };
    if (!profile) pendingSignIn.push(observation);
    else { queued.push(observation); save(); void flush(); }
  });
}
